import {AttrsSchema, Component} from 'panel';
import range from 'lodash/range';

import {DEFAULT_CHART_HEIGHT} from 'common/report/constants';
import {getChartSegmentColorCombo} from 'common/util/colors';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {LabelTheme, Section, SectionItem, TooltipInfo} from 'common/components/items-menu/types';
import {IronContextRegistry} from 'common/report/context-registry';

import {TooltipAttrs} from '../tooltip/types';
import {ChartLegendTheme, MAX_SEGMENTS_PER_COMPACT_TABLE_ROW} from './constants';
import {template} from './template';
import css from './index.inline.styl';

interface ChartLegendState {
  hiddenSeries: string[];
  searchQuery: string;
  showChartTooltip: boolean;
}

interface ChartLegendAttributes {
  'background-color'?: string;
  'enable-search'?: boolean;
  sections?: Section[];
  segments?: string[];
  'show-select-all'?: boolean;
  height?: string;
  'wrap-labels'?: boolean;
  'read-only'?: boolean;
  loading?: boolean;
  'max-legend-items'?: number;
  'max-table-item-width'?: string;
  theme?: ChartLegendTheme;
  'select-all-disabled'?: boolean;
  'select-all-tooltip'?: TooltipInfo;
  tooltip?: TooltipAttrs;
}

export interface ToggleCheckboxDetail<T = any> {
  hiddenSeries: string[];
  isSelectAll: boolean;
  item?: SectionItem<T>;
}

export interface ChartLegendJsxProps {
  attrs?: ChartLegendAttributes;
  on?: {
    togglecheckbox?: (ev: CustomEvent<ToggleCheckboxDetail>) => void;
  };
}

const SEARCH_HEIGHT = 56;

export class ChartLegend extends Component<
  ChartLegendState,
  ChartLegendAttributes,
  unknown,
  unknown,
  IronContextRegistry
> {
  public static get attrsSchema(): AttrsSchema<ChartLegendAttributes> {
    return {
      ...super.attrsSchema,
      'background-color': {type: `string`, description: `overrides the background color of the legend`},
      'enable-search': {type: `boolean`, description: `enable search box`},
      sections: {
        type: `json`,
        default: null,
        description: `explicit section data containing information about color and selected segments`,
      },
      segments: {type: `json`, default: [], description: `array of segment names`},
      'show-select-all': {type: `boolean`, description: `show select all checkbox`},
      'select-all-disabled': `boolean`,
      'select-all-tooltip': `json`,
      height: {
        type: `string`,
        default: null,
        description: `height of the legend, required for progressive functionality`,
      },
      'max-legend-items': {
        type: `number`,
        description: `Maximum number of legend sections to show. Extra sections will be rolled up into an 'Other' label.`,
      },
      'max-table-item-width': {
        type: `string`,
        default: null,
        description: `Maximum width of each legend table item. Only applicable to the "card-table" theme.`,
      },
      'wrap-labels': {
        type: `boolean`,
        description: `wrap each item so that they fit on multiple lines`,
      },
      'read-only': {type: `boolean`, description: `minimal download friendly display mode`},
      loading: {type: `boolean`, description: `show legend loading styles`},
      theme: {type: `string`, enum: Object.values(ChartLegendTheme), default: ChartLegendTheme.Primary},
      tooltip: `json`,
    };
  }

  private _sections: Section[] | null = null;
  get sections(): Section[] | null {
    return this._sections;
  }
  set sections(value: Section[] | null) {
    this._sections = value;
    this.update();
  }

  public get config() {
    return {
      css,
      useShadowDom: true,
      template,
      defaultState: {
        hiddenSeries: [],
        searchQuery: ``,
        showChartTooltip: false,
      },
      contexts: [`i18n` as const],
      helpers: {
        MAX_SEGMENTS_PER_COMPACT_TABLE_ROW,
        ChartLegendTheme,
        range,
        /** @deprecated I think this method is only called when loading-- the
         * sections prop (see getter / setter) is normally set */
        getSections: (): Section[] => {
          if (this.attr(`loading`)) {
            return [];
          }

          const segments = this.attr(`segments`);
          const items: SectionItem[] = segments.map((segment, segmentIdx) => {
            const colorIdx = segmentIdx % segments.length;
            const color = getChartSegmentColorCombo(colorIdx).primary;
            return {
              color,
              label: segment,
              labelTheme: LabelTheme.Soft,
              isSelected: !this.state.hiddenSeries.includes(segment),
              wrapLabel: this.attr(`wrap-labels`),
            };
          });
          return [
            {
              items,
            },
          ];
        },

        toggledAllCheckboxes: () => {
          const hiddenSeries = this.state.hiddenSeries.length > 0 ? [] : this.attr(`segments`);
          this.update({hiddenSeries});
          this.dispatchEvent(
            new CustomEvent(`togglecheckbox`, {
              detail: {
                hiddenSeries,
                isSelectAll: true,
              },
            }),
          );
        },

        toggledCheckbox: (ev) => {
          const {item} = ev.detail;
          const originalHiddenSeries = this.state.hiddenSeries;
          let hiddenSeries = originalHiddenSeries;
          if (originalHiddenSeries.includes(item.label)) {
            // make it visible since it's already hidden
            hiddenSeries = originalHiddenSeries.filter((value) => value !== item.label);
          } else {
            // or make it hidden since it's not already
            hiddenSeries.push(item.label);
          }
          this.update({hiddenSeries});
          this.dispatchEvent(
            new CustomEvent(`togglecheckbox`, {
              detail: {
                hiddenSeries,
                isSelectAll: false,
                item,
              },
            }),
          );
        },

        updateLegendSearch: (ev) => {
          this.update({searchQuery: ev.target.value.toLowerCase()});
        },

        overrideStyle: () => {
          return {
            ...(this.attr(`background-color`) && {background: this.attr(`background-color`)}),
          };
        },

        filterSelected: (sections) => {
          return sections.reduce((filtered, section) => {
            const selectedInSection = section.items.filter((item) => item.isSelected);
            if (selectedInSection.length) {
              return [...filtered, {...section, items: selectedInSection}];
            }
            return filtered;
          }, []);
        },

        getItemsMenuHeight: () => {
          const fromTop = this.attr(`enable-search`) ? SEARCH_HEIGHT : 0;
          const height = this.attr(`height`) || DEFAULT_CHART_HEIGHT;
          return `calc(${height} - ${fromTop}px)`;
        },
      },
    };
  }

  elRefs: {[ref: string]: Node} = {};

  setElRef(ref: string, el: Node) {
    this.elRefs[ref] = el;
  }
}

export const ChartLegendEl = defineAndMakeJsxFactory<ChartLegendJsxProps>(`mp-chart-legend`, ChartLegend);
export const ChartLegendReact = panel2react<ChartLegendJsxProps>(`mp-chart-legend`, ChartLegend);
